/**
 * CollectionPicker - Dropdown for selecting a collection/folder
 */
import { FolderIcon } from '@heroicons/react/20/solid';
import { useMemo } from 'react';

import {
  Listbox,
  ListboxLabel,
  ListboxOption,
} from '@/components/ui/catalyst/listbox';
import { cn } from '@/lib/utils';
import { useGetCollectionTreeQuery } from '@/services/api/collectionService';
import type { CollectionNode } from '@/services/types/collection-types';

interface CollectionPickerProps {
  /** Matter ID to fetch collections for */
  matterId: string;
  /** Currently selected collection ID */
  selectedCollectionId: string | null;
  /** Callback when selection changes */
  onSelectionChange: (collectionId: string) => void;
  /** Optional class name */
  className?: string;
}

interface FlattenedCollection {
  id: string;
  name: string;
  depth: number;
  path: string;
}

/**
 * Flatten the collection tree for the listbox
 */
const flattenCollectionTree = (
  node: CollectionNode,
  depth = 0,
  parentPath = '',
): FlattenedCollection[] => {
  const currentPath = parentPath ? `${parentPath} / ${node.name}` : node.name;
  const result: FlattenedCollection[] = [
    {
      id: node.id,
      name: node.name,
      depth,
      path: currentPath,
    },
  ];

  if (node.children) {
    node.children.forEach((child) => {
      result.push(...flattenCollectionTree(child, depth + 1, currentPath));
    });
  }

  return result;
};

export const CollectionPicker = ({
  matterId,
  selectedCollectionId,
  onSelectionChange,
  className,
}: CollectionPickerProps) => {
  const { data: collectionTree, isLoading } = useGetCollectionTreeQuery(
    matterId,
    { skip: !matterId },
  );

  const flattenedCollections = useMemo(() => {
    if (!collectionTree) return [];
    return flattenCollectionTree(collectionTree);
  }, [collectionTree]);

  if (isLoading) {
    return (
      <div
        className={cn(
          'h-9 px-3 bg-gray-100 rounded-lg flex items-center text-sm text-gray-400',
          className,
        )}
      >
        Loading folders...
      </div>
    );
  }

  return (
    <Listbox
      value={selectedCollectionId || ''}
      onChange={onSelectionChange}
      placeholder={
        <span className="flex items-center gap-2 text-gray-500">
          <FolderIcon className="w-4 h-4" />
          Select a folder...
        </span>
      }
      className={className}
    >
      {flattenedCollections.map((collection) => (
        <ListboxOption key={collection.id} value={collection.id}>
          <span
            className="flex items-center gap-2"
            style={{ paddingLeft: `${collection.depth * 12}px` }}
          >
            <FolderIcon
              className={cn(
                'w-4 h-4 flex-shrink-0',
                collection.id === selectedCollectionId
                  ? 'text-indigo-500'
                  : 'text-gray-400',
              )}
            />
            <ListboxLabel>{collection.name}</ListboxLabel>
          </span>
        </ListboxOption>
      ))}
    </Listbox>
  );
};

export default CollectionPicker;
